import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

import { AuthContext } from './AuthContext';
import { ReportIssueContainer } from '../report-issue/ReportIssueContainer';
import { getStoredToken, removeStoredToken } from '../tokenStorageUtil';
import {
    DIGITAL_SIGNATURE_STATUS_QUERY_PARAM,
    DIGITAL_SIGNATURE_STATUS_COMPLETED,
} from '../digitalSignature/DigitalSignatureConstants';

/**
 * Higher-Order Component that wraps a component with cookie-aware authentication logic.
 *
 * This HOC handles:
 * - Token restoration from storage
 * - Session validation via provided validation function
 * - Digital signature completion flow
 * - Query parameter cleanup
 * - Error handling and token cleanup
 *
 * @param {Object} config - Configuration object for the HOC
 * @param {Function} config.validateSession - Async function to validate the session.
 * @param {Function} [config.onDigitalSignatureComplete] - Optional function to execute some tasks after digital signature completion.
 *
 * @param {Function} config.reportIssueFunc - Function to handle issue reporting
 * @param {string} config.emailLink - Email link for the report issue component
 * @param {React.Component} config.ChildComponent - The component to render after authentication check
 *
 * @returns {React.Component} A component that wraps the ChildComponent with authentication logic
 */
export const withCookieAwareAuth = ({
    validateSession,
    onDigitalSignatureComplete,
    reportIssue,
    emailLink,
    ChildComponent,
}) => {
    const CookieAwareAuthContainer = (props) => {
        const history = useHistory();
        const queryParams = new URLSearchParams(window.location.search);
        const digitalSignatureStatus = queryParams.get(
            DIGITAL_SIGNATURE_STATUS_QUERY_PARAM
        );

        const [cookieValidityCheckDone, setCookieValidityCheckDone] =
            useState(false);

        const routeMatch = useRouteMatch();
        const { url } = routeMatch.params;

        const authContext = useContext(AuthContext);
        const { auth, setAuth } = authContext;

        const token = getStoredToken();

        /**
         * Effect 1: Restore token from storage if it exists
         *
         * On mount, if a token exists in storage, set it in the auth context.
         * This allows the user to maintain their session across page refreshes.
         */
        useEffect(() => {
            if (token != null) {
                // set authorization if a token exists
                setAuth({
                    ...auth,
                    token: `Bearer ${token}`,
                });
            } else {
                setCookieValidityCheckDone(true);
            }
        }, []);

        /**
         * Effect 2: Validate session and handle digital signature completion (extensible to handle other flows as well)
         *
         * Once the token is set, validate the session by calling the provided
         * validateSession function. If valid, mark as authenticated.
         *
         * If the user just completed digital signature (identified by query param),
         * invoke the post-signature handler and clean up query parameters.
         */
        useEffect(() => {
            if (auth.token != null && !auth.isAuthenticated) {
                validateSession(url, authContext)
                    .then(() => {
                        setAuth({
                            ...auth,
                            isAuthenticated: true,
                        });

                        // Handle post-digital-signature flow
                        if (
                            digitalSignatureStatus ===
                            DIGITAL_SIGNATURE_STATUS_COMPLETED
                        ) {
                            // If a post-signature handler is provided, invoke it with props
                            if (onDigitalSignatureComplete) {
                                onDigitalSignatureComplete(props);
                            }

                            // Clean up query params appended by the digital signature provider
                            queryParams.delete(
                                DIGITAL_SIGNATURE_STATUS_QUERY_PARAM
                            );
                            history.replace({ search: queryParams.toString() });
                        }
                    })
                    .catch(() => {
                        // Session validation failed - clean up stored token
                        removeStoredToken();
                    })
                    .finally(() => {
                        setCookieValidityCheckDone(true);
                    });
            }
        }, [auth.token]);

        // Show nothing while checking cookie validity
        if (!cookieValidityCheckDone) {
            return null;
        }

        return (
            <div>
                <ReportIssueContainer
                    reportIssue={reportIssue}
                    emailLink={emailLink}
                />
                <ChildComponent {...props} />
            </div>
        );
    };

    return CookieAwareAuthContainer;
};
